import React, { useState, useCallback } from 'react';
import { InviteApi } from '../../services/api/InviteApi';
import { useData } from '../../contexts/DataContext';
import '../Modal.css';
import './JoinCourseModal.css';

/**
 * JoinCourseModal — Enter an invite code to validate and join a shared course.
 * Reusable across dashboard and onboarding.
 */
export default function JoinCourseModal({ isOpen, onClose, onJoined }) {
  const [code, setCode] = useState('');
  const [step, setStep] = useState('input'); // 'input' | 'preview' | 'joining' | 'success'
  const [coursePreview, setCoursePreview] = useState(null);
  const [error, setError] = useState(null);
  const { loadEnrollments } = useData();

  const resetState = useCallback(() => {
    setCode('');
    setStep('input');
    setCoursePreview(null);
    setError(null);
  }, []);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleValidate = async () => {
    if (!code.trim()) return;
    setError(null);
    try {
      const resp = await InviteApi.validateInvite(code.trim().toUpperCase());
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        setError(text || 'Invalid or expired invite code.');
        return;
      }
      const data = await resp.json();
      setCoursePreview(data);
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Failed to validate invite code.');
    }
  };

  const handleJoin = async () => {
    setStep('joining');
    setError(null);
    try {
      const resp = await InviteApi.redeemInvite(code.trim().toUpperCase());
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        setError(text || 'Failed to join course.');
        setStep('preview');
        return;
      }
      setStep('success');
      // Refresh enrollment data
      if (loadEnrollments) await loadEnrollments();
      // Notify parent
      if (onJoined) {
        const result = await resp.json().catch(() => ({}));
        onJoined(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to join course.');
      setStep('preview');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content join-course-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Join a Course</h2>
          <button className="modal-close-btn" onClick={handleClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body">
          {step === 'input' && (
            <>
              <p className="join-course-desc">Enter the invite code shared by your instructor or classmate.</p>
              <div className="join-code-input-row">
                <input
                  type="text"
                  className="join-code-input"
                  placeholder="e.g. ABC12345"
                  value={code}
                  onChange={e => setCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === 'Enter' && handleValidate()}
                  maxLength={20}
                  autoFocus
                />
                <button className="btn btn-primary" onClick={handleValidate} disabled={!code.trim()}>
                  Validate
                </button>
              </div>
            </>
          )}

          {step === 'preview' && coursePreview && (
            <div className="join-course-preview">
              <div className="join-preview-card">
                <h3>{coursePreview.endeavorName || coursePreview.courseName || 'Course'}</h3>
                {coursePreview.courseCode && (
                  <span className="join-preview-code">{coursePreview.courseCode}</span>
                )}
                {coursePreview.description && (
                  <p className="join-preview-description">{coursePreview.description}</p>
                )}
                {coursePreview.instructor && (
                  <p className="join-preview-meta">Instructor: {coursePreview.instructor}</p>
                )}
                {coursePreview.enrollmentCount != null && (
                  <p className="join-preview-meta">{coursePreview.enrollmentCount} students enrolled</p>
                )}
              </div>
              <div className="join-course-actions">
                <button className="btn btn-secondary" onClick={() => { setStep('input'); setError(null); }}>
                  ← Back
                </button>
                <button className="btn btn-primary" onClick={handleJoin}>
                  Join Course
                </button>
              </div>
            </div>
          )}

          {step === 'joining' && (
            <div className="join-course-status">
              <div className="loading-spinner" />
              <p>Joining course...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="join-course-status join-course-success">
              <span className="join-success-icon">✓</span>
              <h3>You're in!</h3>
              <p>You've successfully joined the course.</p>
              <button className="btn btn-primary" onClick={handleClose}>
                Done
              </button>
            </div>
          )}

          {error && (
            <p className="join-course-error">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}
